import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';

/**
 * ExpandableText - Component for text that can be collapsed/expanded
 * Shows up to 2 lines by default with a chevron icon on the right to expand
 *
 * @example
 * ```gts
 * <ExpandableText @text={{longText}} class="tio-fg-secondary" />
 * ```
 */
export default class ExpandableText extends Component<{
  Element: HTMLDivElement;
  Args: {
    text: string | number | boolean | null;
  };
}> {
  @tracked isExpanded = false;

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  // Convert text to string for display
  get displayText(): string {
    const { text } = this.args;
    if (text === null || text === undefined) return '';
    return String(text);
  }

  // Show expand/collapse button if text is longer than ~150 characters
  get shouldShowToggle() {
    return this.displayText && this.displayText.length > 150;
  }

  <template>
    <div class="tio-row-top-tight" ...attributes>
      <div class="flex-1 {{if this.isExpanded '' 'line-clamp-2'}}">
        {{this.displayText}}
      </div>
      {{#if this.shouldShowToggle}}
        <button
          type="button"
          class="shrink-0 tio-fg-muted hover:tio-fg-secondary transition-colors"
          {{on "click" this.toggleExpanded}}
          aria-label={{if this.isExpanded "Show less" "Show more"}}
        >
          {{#if this.isExpanded}}
            <PhCaretUp @size="16" />
          {{else}}
            <PhCaretDown @size="16" />
          {{/if}}
        </button>
      {{/if}}
    </div>
  </template>
}
